import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import styles from './index.module.scss';
import {isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';
import {NavLink, Outlet} from 'react-router';
import {useAppDispatch} from '@/Pages/Common/hooks/useAppDispatch';
import {useAppSelector} from '@/Pages/Common/hooks/useAppSelector';
import {
  Autocomplete,
  Button,
  Checkbox,
  Divider,
  Dropdown,
  DropdownItem,
  Grid,
  GridCol,
  ListBoxOptionType,
  NavHorizontal,
  NavItem,
  PageContainer,
  Select,
  SelectOption,
  spaceHelpers
} from '@flixbus/honeycomb-react';
import {
  Icon,
  IconDelete,
  IconDeleteSolid,
  IconOfferSolid
} from '@flixbus/honeycomb-icons-react';
import ContentLoader from '@/Pages/Common/ContentLoader';
import {confirm} from '@/Pages/Common/Confirm';
import {useToast} from '@/Pages/Common/toasts-container';
import {setPartner} from '@/Pages/Partners/store/slice';
import PartnerPanel from '@/Pages/Partners/components/PartnerPanel';
import {VehicleTypeCostsPanelState} from '@/components/VehicleTypeCosts/VehicleTypeCostsPanel/VehicleTypeCostsPanel.types';
import {fetchBusPartnerAndCountryCostEntryYear} from '../store/thunks';
import {resetBusPartnerSpecificData} from '../store/slice';
import navHorizontalOptions from '@/Pages/PartnerCosts/constants/navHorizontalOptions';
import {useUpdateBusPartnerQualityCheck} from '@/Pages/PartnerCosts/hooks/useUpdateBusPartnerQualityCheck';
import PartnerCostsVehicleCostPanel from '@/Pages/PartnerCosts/components/PartnerCostsVehicleCostTab/PartnerCostsVehicleTypeCostsPanel/PartnerCostsVehicleTypeCostsPanel';
import {EPartnerCostsNewYearDataSource} from './index.types';
import {useLocalUnitsContext} from '@/Pages/Common/LocalUnits';
import PowerBiReportLink from '@/components/PowerBiReportLink/PowerBiReportLink';
import {
  POWER_BI_REPORT_BUS_PARTNER_COMPARE_URL,
  POWER_BI_REPORT_BUS_PARTNER_YOY_URL
} from '@/constants/PowerBiReportUrls';
import classNames from 'classnames';
import {useBusPartnerCostsQueryParams} from '../hooks/useBusPartnerCostsQueryParams';
import {useBusPartners} from '@/Pages/Partners/hooks/useBusPartners';
import {useCountries} from '@/Pages/Countries/hooks/useCountries';
import {displaySuccessMessage} from '@/Pages/Common/Utils';
import {Partner} from '@/Pages/Partners/store/types';
import {useBusPartnersCostYears} from '../hooks/useBusPartnersCostYears';

import {useAddPartnerCostEntryYear} from '../hooks/useAddPartnerCostEntryYear';
import {useClonePartnerCostEntryYear} from '../hooks/useClonePartnerCostEntryYear';
import {useDeletePartnerCostEntryYear} from '../hooks/useDeletePartnerCostEntryYear';

const PartnerCosts: React.FC = () => {
  const vehicleTypes = useAppSelector(({vehicleTypes}) => vehicleTypes.list);
  const qualityChecked = useAppSelector(
    ({partnerCosts}) => partnerCosts.qualityChecked
  );
  const dispatch = useAppDispatch();

  const {1: space1, 4: space4} = spaceHelpers();
  const {addToast} = useToast();
  const {localUnits} = useLocalUnitsContext();
  const addNewYearButtonRef = useRef(null);
  const [panelState, setPanelState] = useState<VehicleTypeCostsPanelState>({
    data: null,
    loading: false,
    error: null
  });

  const {countries} = useCountries();
  const {busPartners, refetchBusPartners} = useBusPartners();
  const {busPartnersCostYears, refetchBusPartnersCostYears} =
    useBusPartnersCostYears();
  const {busPartnerId, handleChangeBusPartnerId, year, handleChangeYear} =
    useBusPartnerCostsQueryParams();

  const {addPartnerCostEntryYear} = useAddPartnerCostEntryYear();
  const {clonePartnerCostEntryYear} = useClonePartnerCostEntryYear();
  const {deletePartnerCostEntryYear} = useDeletePartnerCostEntryYear();

  const busPartner = useMemo(
    () => busPartners?.find(busPartner => busPartner.id === busPartnerId),
    [busPartnerId, busPartners]
  );
  const busPartnerCountry = useMemo(
    () => countries?.find(country => country.id === busPartner?.countryId),
    [busPartner?.countryId, countries]
  );

  const [busPartnerFilter, setBusPartnerFilter] = useState('');
  useEffect(() => {
    setBusPartnerFilter(busPartner?.name ?? '');
  }, [busPartner]);

  const onClosePanel = useCallback(
    async (partner?: Partner) => {
      // refetch bus partner if a bus partner was successfully created or updated
      if (partner) {
        await Promise.all([
          refetchBusPartners(),
          refetchBusPartnersCostYears()
        ]);
        handleChangeBusPartnerId(partner.id);
      }
    },
    [handleChangeBusPartnerId, refetchBusPartners, refetchBusPartnersCostYears]
  );

  const partnerOptions: ListBoxOptionType[] = useMemo(
    () =>
      (busPartners ?? []).map(busPartner => ({
        title: busPartner.name,
        value: busPartner.id
      })),
    [busPartners]
  );
  const [filteredPartnerOptions, setFilteredPartnerOptions] = useState<
    ListBoxOptionType[]
  >([]);

  useEffect(() => {
    dispatch(
      fetchBusPartnerAndCountryCostEntryYear(
        busPartnerId,
        year,
        busPartner?.countryId
      )
    );
  }, [busPartnerId, year, busPartner?.countryId, dispatch]);

  const ignoreQualityCheckValidationConfirmation = useCallback(
    () =>
      confirm({
        title: 'Some mandatory fields are empty',
        message:
          'Please go back and specify values at either regional or partner level',
        acceptBtnText: 'Proceed anyway',
        cancelBtnText: 'Go back'
      }),
    []
  );

  const {updateQualityCheck} = useUpdateBusPartnerQualityCheck(
    ignoreQualityCheckValidationConfirmation
  );

  const addNewBlankYear = useCallback(
    (busPartnerId: string) => {
      const callback = (year: number) => {
        displaySuccessMessage(
          addToast,
          `${year} partner costs database with blank values created successfully`
        );
        handleChangeYear(year);
      };
      addPartnerCostEntryYear(busPartnerId, callback);
    },
    [addPartnerCostEntryYear, addToast, handleChangeYear]
  );

  const clonePreviousYear = useCallback(
    (busPartnerId: string) => {
      const callback = (year: number) => {
        displaySuccessMessage(
          addToast,
          `${year} partner costs database with values from previous year created successfully`
        );
        handleChangeYear(year);
      };
      clonePartnerCostEntryYear(busPartnerId, callback);
    },
    [addToast, clonePartnerCostEntryYear, handleChangeYear]
  );

  const onAddPartnerCostEntryYear = useCallback(
    (dataSource: EPartnerCostsNewYearDataSource) => {
      // This is a hack to trigger the dropdown to close
      if (addNewYearButtonRef.current) {
        (addNewYearButtonRef.current as HTMLButtonElement).click();
      }

      if (!busPartnerId) return;

      if (dataSource === EPartnerCostsNewYearDataSource.previousYear) {
        clonePreviousYear(busPartnerId);
      } else {
        addNewBlankYear(busPartnerId);
      }
    },
    [clonePreviousYear, addNewBlankYear, busPartnerId]
  );

  const onDeletePartnerCostEntryYear = useCallback(
    async (busPartnerId: string | null, year: number | null) => {
      if (!busPartnerId || !year) return;

      if (
        await confirm({
          PopupIcon: IconDelete,
          title: `You are about to delete all cost data for partner ${
            busPartner?.name ?? ''
          } and year ${year}`,
          message: `Do you wish to proceed ?`
        })
      ) {
        const callback = (year: number) => {
          displaySuccessMessage(
            addToast,
            `Bus partner cost year ${year} deleted successfully`
          );
        };

        deletePartnerCostEntryYear(busPartnerId, year, callback);
      }
    },
    [addToast, busPartner?.name, deletePartnerCostEntryYear]
  );

  const handleDebounce = useCallback(
    async (inputValue: string) => {
      const searchedText = inputValue.toLowerCase();

      if (!searchedText) {
        setFilteredPartnerOptions([]);
        return [];
      }

      const filteredPartners = partnerOptions
        .filter(partner => partner.title.toLowerCase().startsWith(searchedText))
        .sort((p1, p2) => p1.title.localeCompare(p2.title));
      setFilteredPartnerOptions(filteredPartners);

      return filteredPartners;
    },
    [partnerOptions]
  );

  const handleSelect = useCallback(
    (selectedOptions: ListBoxOptionType[]) => {
      dispatch(resetBusPartnerSpecificData());

      const busPartner = selectedOptions[0];
      handleChangeBusPartnerId((busPartner?.value as string) ?? null);

      setFilteredPartnerOptions([]);
    },
    [dispatch, handleChangeBusPartnerId]
  );

  const renderOption = useCallback(
    (option: ListBoxOptionType) => option.title,
    []
  );

  const yearsOptions = useMemo(() => {
    const years =
      busPartnersCostYears?.find(
        busPartnerYears => busPartnerYears.busPartnerId === busPartnerId
      )?.years ?? [];
    return years.map(year => (
      <SelectOption key={year} value={year}>
        {year}
      </SelectOption>
    ));
  }, [busPartnerId, busPartnersCostYears]);

  if (!busPartners) return <ContentLoader />;

  return (
    <PageContainer extraClasses="max-width-1500">
      <div className="header-section">
        <Grid>
          <GridCol size={3}>
            <div className="partner-costs-partners">
              <div>
                <Autocomplete
                  comboboxProps={{
                    label: 'Partner',
                    onChange: e => setBusPartnerFilter(e.target.value)
                  }}
                  inputValue={busPartnerFilter}
                  options={filteredPartnerOptions}
                  listBoxProps={{
                    optionsToDisplay: 5,
                    optionProps: (option: ListBoxOptionType) => ({
                      renderOption: () => renderOption(option)
                    })
                  }}
                  onDebounce={handleDebounce}
                  onChange={handleSelect}
                />
              </div>
              <div>
                <Button
                  display="square"
                  aria-label="New partner"
                  onClick={() => dispatch(setPartner(null))}
                >
                  <Icon InlineIcon={IconOfferSolid} aria-hidden="true" />
                </Button>
              </div>
            </div>
          </GridCol>
          <GridCol size={3}>
            <div className="partner-costs-years">
              <div>
                <Select
                  disabled={isNil(busPartnerId)}
                  id="partner-costs-years"
                  label="Calendar year"
                  value={year === null ? '' : year}
                  placeholder="Select a year"
                  onChange={e => {
                    dispatch(resetBusPartnerSpecificData());
                    handleChangeYear(Number(e.target.value));
                  }}
                >
                  {yearsOptions}
                </Select>
              </div>
              <div className={styles.yearActions}>
                <Dropdown
                  links={[
                    <DropdownItem
                      key="copy-from-previous-year"
                      Elem="button"
                      onClick={() =>
                        onAddPartnerCostEntryYear(
                          EPartnerCostsNewYearDataSource.previousYear
                        )
                      }
                    >
                      Copy from previous year
                    </DropdownItem>,
                    <DropdownItem
                      key="start-with-blank-values"
                      Elem="button"
                      onClick={() =>
                        onAddPartnerCostEntryYear(
                          EPartnerCostsNewYearDataSource.blankValues
                        )
                      }
                    >
                      Start with blank values
                    </DropdownItem>
                  ]}
                >
                  <Button
                    display="square"
                    aria-label="New year"
                    disabled={isNil(busPartnerId)}
                    innerRef={addNewYearButtonRef}
                  >
                    <Icon InlineIcon={IconOfferSolid} aria-hidden="true" />
                  </Button>
                </Dropdown>
                <Button
                  display="square"
                  aria-label="Delete year"
                  disabled={isNil(busPartnerId) || isNilOrEmpty(year)}
                  onClick={() =>
                    onDeletePartnerCostEntryYear(busPartnerId, year)
                  }
                >
                  <Icon InlineIcon={IconDeleteSolid} aria-hidden="true" />
                </Button>
              </div>
            </div>
          </GridCol>
        </Grid>

        <div className={space4.top}>
          <Grid align="center" extraClasses={year ? '' : 'hidden'}>
            <GridCol key="grid-nested-checkbox" size={4}>
              <Checkbox
                id="partner-costs-year-quality-check"
                checked={qualityChecked}
                value={qualityChecked.toString()}
                aria-label="partner-costs-year-quality-check"
                label={year + ` partner costs database completed`}
                onChange={event => updateQualityCheck(event.target.checked)}
              />
            </GridCol>
            <GridCol
              key="grid-nested-link"
              size={8}
              extraClasses={classNames('justify-end', 'gap-6')}
            >
              <PowerBiReportLink
                href={POWER_BI_REPORT_BUS_PARTNER_YOY_URL}
                page="Partner Costs"
                label="Analyse YoY development"
              />
              <PowerBiReportLink
                href={POWER_BI_REPORT_BUS_PARTNER_COMPARE_URL}
                page="Partner Costs"
                label="Compare to other BPs"
              />
            </GridCol>
          </Grid>
        </div>

        <Grid>
          <GridCol size={12}>
            <div className={`${space4.top} nav-horizontal-container`}>
              <NavHorizontal
                extraClasses={space1.bottom}
                aria-label="Partner Costs Navigation"
              >
                {navHorizontalOptions.map(item => (
                  <NavItem
                    key={item.path}
                    to={{pathname: `/${item.path}`, search: location.search}}
                    RouterLink={NavLink}
                    end
                  >
                    {item.name}
                  </NavItem>
                ))}
              </NavHorizontal>
            </div>
          </GridCol>
        </Grid>
      </div>

      <Divider extraClasses={'full-width-divider'} />

      <div className={space4.top}>
        <Outlet />
      </div>

      <PartnerPanel onClose={onClosePanel} />
      {busPartnerCountry && vehicleTypes && (
        <PartnerCostsVehicleCostPanel
          partnerId={busPartnerId}
          year={year}
          currency={busPartner?.currency ?? ''}
          vehicleTypes={vehicleTypes}
          localUnits={localUnits}
          panelState={panelState}
          setPanelState={setPanelState}
          partnerCountry={busPartnerCountry}
        />
      )}
    </PageContainer>
  );
};

export default React.memo(PartnerCosts);
